import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, euros } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import ImagePicker from "@/components/admin/ImagePicker";

export default function PricingAdmin() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pricing").then((r) => setP(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!p) return <p className="text-muted-foreground">Chargement…</p>;

  const promo = p.promo || {};
  const setPromo = (k, v) => setP({ ...p, promo: { ...promo, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        price_1: Number(p.price_1), price_2: Number(p.price_2),
        compare_at_2: Number(p.compare_at_2) || 0,
        product_description: p.product_description || "",
        product_features: p.product_features || "",
        product_image: p.product_image || "",
        offers_available: p.offers_available !== false,
        promo: {
          active: !!promo.active,
          price_1: promo.price_1 ? Number(promo.price_1) : null,
          price_2: promo.price_2 ? Number(promo.price_2) : null,
          label: promo.label || "", start: promo.start || "", end: promo.end || "",
        },
      };
      const { data } = await api.put("/admin/pricing", payload);
      setP(data);
      toast.success("Tarifs enregistrés");
    } catch (e) {
      toast.error(apiError(e, "Enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-pricing" className="max-w-3xl">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx mb-8">Produits & Tarifs</h1>

      <section className="bg-white border border-line p-6 sm:p-8 mb-6">
        <h2 className="font-display font-bold text-lg text-onyx mb-6">Offres</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="price-1">Prix 1 jeu (€ TTC)</Label>
            <Input id="price-1" type="number" step="0.01" min="0" data-testid="price-1-input" className="mt-1.5"
              value={p.price_1} onChange={(e) => setP({ ...p, price_1: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="price-2">Prix 2 jeux (€ TTC)</Label>
            <Input id="price-2" type="number" step="0.01" min="0" data-testid="price-2-input" className="mt-1.5"
              value={p.price_2} onChange={(e) => setP({ ...p, price_2: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="compare-2">Prix barré 2 jeux (€)</Label>
            <Input id="compare-2" type="number" step="0.01" min="0" data-testid="compare-2-input" className="mt-1.5"
              value={p.compare_at_2} onChange={(e) => setP({ ...p, compare_at_2: e.target.value })} />
            <p className="text-xs text-muted-foreground mt-1">Avantage affiché : {euros((Number(p.compare_at_2) || 0) - (Number(p.price_2) || 0))}</p>
          </div>
        </div>
      </section>

      <section className="bg-white border border-line p-6 sm:p-8 mb-6">
        <h2 className="font-display font-bold text-lg text-onyx mb-6">Produit</h2>
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <Switch data-testid="offers-available-switch" checked={p.offers_available !== false}
              onCheckedChange={(v) => setP({ ...p, offers_available: v })} />
            <Label>Disponible à la commande en ligne</Label>
          </div>
          <ImagePicker label="Image produit (page Acheter)" value={p.product_image || ""} onChange={(url) => setP({ ...p, product_image: url })} />
          <div>
            <Label>Description produit</Label>
            <Textarea rows={3} data-testid="product-description-input" className="mt-1.5"
              value={p.product_description || ""} onChange={(e) => setP({ ...p, product_description: e.target.value })} />
          </div>
          <div>
            <Label>Caractéristiques (une par ligne)</Label>
            <Textarea rows={3} data-testid="product-features-input" className="mt-1.5" placeholder={"2 à 6 joueurs\nDès 6 ans\n45 minutes"}
              value={p.product_features || ""} onChange={(e) => setP({ ...p, product_features: e.target.value })} />
          </div>
        </div>
      </section>

      <section className="bg-white border border-line p-6 sm:p-8 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-bold text-lg text-onyx">Promotion</h2>
          <div className="flex items-center gap-3">
            <Label htmlFor="promo-active" className="text-sm">Active</Label>
            <Switch id="promo-active" data-testid="promo-active-switch" checked={!!promo.active}
              onCheckedChange={(v) => setPromo("active", v)} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="promo-price-1">Prix promo 1 jeu (€, optionnel)</Label>
            <Input id="promo-price-1" type="number" step="0.01" min="0" data-testid="promo-price-1-input" className="mt-1.5"
              value={promo.price_1 ?? ""} onChange={(e) => setPromo("price_1", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="promo-price-2">Prix promo 2 jeux (€, optionnel)</Label>
            <Input id="promo-price-2" type="number" step="0.01" min="0" data-testid="promo-price-2-input" className="mt-1.5"
              value={promo.price_2 ?? ""} onChange={(e) => setPromo("price_2", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="promo-label">Libellé de la promotion</Label>
            <Input id="promo-label" data-testid="promo-label-input" className="mt-1.5" placeholder="Ex : Offre lancement -15%"
              value={promo.label || ""} onChange={(e) => setPromo("label", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="promo-start">Date de début</Label>
            <Input id="promo-start" type="date" data-testid="promo-start-input" className="mt-1.5"
              value={promo.start || ""} onChange={(e) => setPromo("start", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="promo-end">Date de fin</Label>
            <Input id="promo-end" type="date" data-testid="promo-end-input" className="mt-1.5"
              value={promo.end || ""} onChange={(e) => setPromo("end", e.target.value)} />
          </div>
        </div>
      </section>

      <button onClick={save} disabled={saving} data-testid="pricing-save"
        className="inline-flex items-center gap-2 bg-rouge text-white px-8 py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
        <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}
